import { format, isValid } from 'date-fns';
import type {
  DebtPaymentRequest,
  ManagerMovement,
  MovementType,
  UnifiedMovement,
} from '@/types/manager.model';
import { fromUTC, parseUtcIso } from '@/utils/formatters';
import { getPaymentTypeLabel } from '@/utils/payment-utils';

export const currencyFormatter = new Intl.NumberFormat('es-AR', {
  style: 'currency',
  currency: 'ARS',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const formatDayAndTime = (date: Date | null): string =>
  date && isValid(date) ? format(date, 'dd/MM/yyyy HH:mm') : '—';

const toEpochMs = (value: string | null | undefined): number =>
  parseUtcIso(value)?.getTime() ?? Number.NaN;

/**
 * Server-stamped timestamps: movement dates and the debt-payment `createdAt` /
 * `reviewedAt`. The latter are UTC and carry no offset, so they must not be
 * parsed as browser-local time.
 */
export const formatMovementDate = (value: string | null | undefined): string =>
  formatDayAndTime(parseUtcIso(value));

/**
 * `transferDate` is the one the manager sends itself, as the user's wall clock
 * with no offset, and the backend stores it untouched.
 */
export const formatTransferDate = (value: string | null | undefined): string =>
  formatDayAndTime(value ? fromUTC(value) : null);

const normalize = (value: string | null | undefined) => value?.trim() ?? '';

const isEmptyPlaceholder = (value: string | null | undefined) => {
  const v = normalize(value);
  return v === '' || v === '-';
};

const mapModalidad = (
  type: string | null | undefined,
  tipo: MovementType,
): string | null => {
  if (tipo === 'transferencia') return null;
  const v = normalize(type).toUpperCase();
  if (v === 'TEAM') return 'Equipo';
  if (v === 'INDIVIDUAL') return 'Individual';
  return null;
};

const mapMetodo = (value: string | null | undefined): string | null => {
  if (isEmptyPlaceholder(value)) return null;
  return getPaymentTypeLabel(value as string);
};

type ChargedMovementType = Extract<MovementType, 'torneo' | 'clase' | 'partido'>;

const MOVEMENT_TYPE_BY_CHARGE: Record<string, ChargedMovementType> = {
  CLASE: 'clase',
  PARTIDO: 'partido',
};

const FALLBACK_CONCEPT: Record<ChargedMovementType, string> = {
  clase: 'Clase',
  partido: 'Partido',
  torneo: 'Torneo',
};

/** Tournament/class rows from the movements endpoint. Transfers are ignored here. */
export const mapManagerMovement = (
  m: ManagerMovement,
  index: number,
): UnifiedMovement | null => {
  // Approved transfers arrive as positive MANUAL_TRANSFER entries; we source them
  // from the debt-payments endpoint to avoid duplicates.
  if (m.origin === 'MANUAL_TRANSFER') return null;

  const chargeType = normalize(m.chargeType).toUpperCase();
  const tipo = MOVEMENT_TYPE_BY_CHARGE[chargeType] ?? 'torneo';
  const isRefund = m.origin === 'REFUND';

  const concepto = isEmptyPlaceholder(m.tournamentName)
    ? FALLBACK_CONCEPT[tipo]
    : (m.tournamentName as string);

  return {
    id: `mov-${m.date}-${index}`,
    date: m.date,
    tipo,
    amount: m.amount,
    saldoPosterior: m.currentBalance,
    cliente: isEmptyPlaceholder(m.email) ? null : m.email,
    concepto,
    conceptoSub: isRefund ? 'Reembolso' : null,
    modalidad: mapModalidad(m.registrationPaymentType, tipo),
    metodo: mapMetodo(m.paymentMethod),
    valor: m.tournamentValue ?? null,
    settlement: isRefund ? 'REEMBOLSADO' : 'ACEPTADO',
  };
};

/** Maps a debt cancellation request to a unified history row. */
export const mapDebtPayment = (
  req: DebtPaymentRequest,
  sessionEmail: string | null,
  saldoPosterior: number | null,
): UnifiedMovement => ({
  id: `debt-${req.id}`,
  date: req.reviewedAt ?? req.createdAt ?? '',
  tipo: 'transferencia',
  // A transfer reduces debt → positive impact on the balance.
  amount: req.amount,
  saldoPosterior,
  // The client is the logged-in manager, who initiated the transfer.
  cliente: sessionEmail,
  concepto: 'Cancelación de deuda',
  conceptoSub: null,
  modalidad: null,
  metodo: 'Transferencia bancaria',
  valor: req.amount,
  status: req.status,
  motivoRechazo: req.status === 'REJECTED' ? req.reviewNotes : null,
  receiptUrl: req.receiptUrl,
  requestId: req.id,
});

/** Merges both sources and sorts by date descending. */
export const buildUnifiedMovements = (
  movements: ManagerMovement[],
  debtPayments: DebtPaymentRequest[],
  options: { sessionEmail?: string | null } = {},
): UnifiedMovement[] => {
  const sessionEmail = options.sessionEmail ?? null;

  const fromMovements = movements
    .map(mapManagerMovement)
    .filter((m): m is UnifiedMovement => m !== null);

  // Timeline of real balances (includes approved transfers, which the
  // backend exposes as `MANUAL_TRANSFER` entries).
  const balancePoints = movements
    .filter((m) => m.currentBalance != null && !!m.date)
    .map((m) => ({
      time: toEpochMs(m.date),
      balance: m.currentBalance,
      origin: m.origin,
      amount: m.amount,
    }))
    .sort((a, b) => a.time - b.time);

  const approvedPoints = balancePoints.filter(
    (p) => p.origin === 'MANUAL_TRANSFER',
  );

  // Balance at a given point in time (last point <= date). Since non-approved
  // transfers don't move the balance, the value carries forward unchanged.
  const carryForward = (time: number): number | null => {
    if (Number.isNaN(time)) return null;
    let result: number | null = null;
    for (const point of balancePoints) {
      if (point.time <= time) result = point.balance;
      else break;
    }
    return result;
  };

  const fromTransfers = debtPayments.map((req) => {
    const time = toEpochMs(req.reviewedAt ?? req.createdAt);
    let saldoPosterior: number | null;
    if (req.status === 'APPROVED') {
      // On approval, the backend stores the resulting balance in the MANUAL_TRANSFER entry.
      const match = approvedPoints
        .filter((p) => Math.abs(p.amount - Number(req.amount)) < 0.01)
        .sort(
          (a, b) => Math.abs(a.time - time) - Math.abs(b.time - time),
        )[0];
      saldoPosterior = match ? match.balance : carryForward(time);
    } else {
      saldoPosterior = carryForward(time);
    }
    return mapDebtPayment(req, sessionEmail, saldoPosterior);
  });

  return [...fromMovements, ...fromTransfers]
    .map((row) => {
      const time = toEpochMs(row.date);
      return { row, time: Number.isNaN(time) ? 0 : time };
    })
    .sort((a, b) => b.time - a.time)
    .map(({ row }) => row);
};
